import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  TimePicker,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  deletePhysioCareAPI,
  physioCareAPI,
  updatePhysioCareAPI,
} from "../../../../services/physio.services";
import { DeleteOutlined } from "@ant-design/icons";
import EditIcon from "../../../ui/EditIcon";
import useQuery from "../../../../hooks/useQuery";
import useIsMobile from "../../../../hooks/useIsMobile";

const TimeFormat = "h:mm a";

const SessionTable = ({
  sessionData,
  setSessionData,
  session,
  date,
  therapies,
  practitioners,
  refreshTables,
  userData,
}) => {
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location?.search));
  const admissionID = params?.patient;
  const [timeDone, setTimeDone] = useState(dayjs());
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const isActive = useQuery()?.status === "current";
  const isMobile = useIsMobile();

  const handleTimeDone = (val) => {
    setTimeDone(val);
  };

  const handleDelete = async (id) => {
    deletePhysioCareAPI(id)
      .then((res) => {
        message.success("Therapy deleted successfully");
        refreshTables();
      })
      .catch((error) => {
        console.log(error);
        message.error("Failed to delete therapy");
      });
  };

  const handleOk = async () => {
    let data = {};
    let hasValidationError = false;
    await form
      .validateFields()
      .then((values) => {
        data = values;
      })
      .catch((error) => {
        hasValidationError = true;
        console.log(error);
      });
    if (hasValidationError) {
      return;
    }
    if (!Array.isArray(data?.therapy)) {
      data.therapy = [data?.therapy];
    }
    const isEdit = !!editData;
    const requests = data.therapy.map((option) => {
      const finalData = {
        ...data,
        therapy: option,
        admission_id: admissionID,
        session,
        session_date: date,
        duration: data?.duration / data?.therapy?.length,
        time_done: timeDone,
      };
      return isEdit
        ? updatePhysioCareAPI(editData, finalData)
        : physioCareAPI(finalData);
    });

    try {
      await Promise.all(requests);
      message.success(
        isEdit ? "Therapy updated successfully" : "Therapy added successfully"
      );
      setOpen(false);
      form.resetFields();
    } catch (error) {
      console.log(`Error:${error}`);
      message.error(
        isEdit ? "Failed to update therapy" : "Failed to add therapy"
      );
    }
    setTimeout(() => {
      refreshTables();
    }, 1000);
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "id",
      key: "id",
      render: (_, record, index) => index + 1,
      width: 100,
    },
    {
      title: "Therapy",
      dataIndex: "therapy",
      key: "therapy",
      render: (id) => therapies?.find((option) => option?.value === id)?.label,
    },
    {
      title: "Done By",
      dataIndex: "done_by",
      key: "done_by",
      render: (id) =>
        practitioners?.find((option) => option?.value === id)?.label,
    },
    {
      title: "Time Done",
      dataIndex: "time_done",
      key: "time_done",
      render: (time) => dayjs(time)?.format(TimeFormat),
      width: 200,
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
    },
    {
      title: "Updated by",
      dataIndex: "updated_by",
      key: "updated_by",
      render: (id) =>
        practitioners?.find((option) => option?.value === id)?.label,
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (time) => dayjs(time)?.format("h:mm:ss a"),
      width: 200,
    },
    ...(isActive
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            key: "actions",
            render: (_, record) => (
              <Space size={4} className="physio-care-table-actions">
                <Button
                  type="text"
                  className="ui-action-icon"
                  icon={<EditIcon size={16} />}
                  onClick={() => {
                    setOpen(true);
                    setEditData(record?.id);
                    form?.setFieldsValue({
                      ...record,
                      time_done: record?.updated_at
                        ? dayjs(record?.updated_at)
                        : dayjs(),
                    });
                  }}
                />
                <Button
                  type="text"
                  danger
                  className="ui-action-icon"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    Modal.confirm({
                      title: "Confirmation",
                      content: "Are you sure you want to Delete",
                      onOk: () => {
                        handleDelete(record?.id);
                      },
                    });
                  }}
                />
              </Space>
            ),
            width: 200,
          },
        ]
      : []),
  ];

  const calculateTotalDuration = () => {
    return sessionData?.reduce((acc, cur) => acc + (cur?.duration || 0), 0);
  };

  const totalDuration = calculateTotalDuration();

  return (
    <Row gutter={[0, 8]}>
      {isActive && (
        <Col span={24} className="flex-end physio-care-add-btn-col">
          <Button
            onClick={() => {
              setEditData(null);
              setOpen(true);
            }}
          >
            Add Therapy
          </Button>
        </Col>
      )}
      {sessionData?.length > 0 && (
        <Col span={24}>
          <Typography className="physio-care-total-duration">
            Total Duration for this session: {totalDuration} min(s)
          </Typography>
        </Col>
      )}
      <Col span={24} className="physio-care-session-table-wrap">
        <Table
          dataSource={sessionData}
          columns={columns}
          pagination={false}
          scroll={isMobile ? undefined : { x: 1200, y: "40vh" }}
        />
      </Col>
      <Modal
        title={"Physio Session Details"}
        open={open}
        keyboard={false}
        onOk={handleOk}
        onCancel={() => {
          setOpen(false);
          form?.resetFields();
        }}
        centered
        wrapClassName="ip-physio-care-modal-wrap"
        width={isMobile ? "min(720px, calc(100vw - 32px))" : "50%"}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Name" name={"therapy"}>
            <Select
              showSearch
              mode="multiple"
              options={therapies}
              allowClear
              disabled={editData}
              placeholder="Search to select"
              optionFilterProp="label"
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item label="Done By" name="done_by">
            <Select
              showSearch
              options={practitioners}
              allowClear
              placeholder="Search to select"
              optionFilterProp="label"
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item label="Duration" name={"duration"}>
            <InputNumber placeholder="in min" />
          </Form.Item>
          <Form.Item label="Comments" name={"comments"}>
            <Input.TextArea rows={4} style={{ width: "70%" }} />
          </Form.Item>
          <Form.Item label="Time Done">
            <TimePicker
              inputReadOnly
              onChange={handleTimeDone}
              format={TimeFormat}
              needConfirm={false}
              value={timeDone}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Row>
  );
};

export default SessionTable;
